<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import CompleteRegistrationEmbed from '~/components/auth/post-reg/complete-registration-embed.vue';
import DocumentVerificaitonEmbed from '~/components/account/settings/document-verificaiton-embed.vue';
import LazyImage from '~/components/design-system/lazy-image.vue';

interface Props {
  validatePartialReg?: boolean;
  validateCompliance?: boolean;
  noticeKey?: string;
  siteSection?: 'rg' | 'banking' | 'gameplay' | 'transactions' | null;
  showCustomImage?: boolean;
}

interface IValidations {
  compliance: boolean;
  partialReg: boolean;
  enableBanking: boolean;
  enableGamePlay: boolean;
  enableRg: boolean;
  enableTransactionsSumary: boolean;
}

const { $enabled } = useNuxtApp();
const authStore = useAuthStore();
const gameStore = useGameStore();

const props = withDefaults(defineProps<Props>(), {
  validateCompliance: false,
  validatePartialReg: false,
  noticeKey: 'generic',
  siteSection: null,
  showCustomImage: false,
});

const validations = computed(() => {
  const validationList: IValidations = {
    compliance: true,
    partialReg: true,
    enableBanking: true,
    enableGamePlay: true,
    enableRg: true,
    enableTransactionsSumary: true,
  };

  if (props.validateCompliance)
    validationList.compliance =
      authStore.getIsUserCompliant &&
      (!authStore.getCbpRequired || !authStore.getSumsubRequired);

  if (props.validatePartialReg)
    validationList.partialReg = authStore.getIsRegistrationComplete;

  switch (props.siteSection) {
    case 'rg':
      validationList.enableRg = !$enabled('website.disableresponsiblegaming');
      break;
    case 'banking':
      validationList.enableBanking = !$enabled('website.disablebanking');
      break;
    case 'gameplay':
      validationList.enableGamePlay = !$enabled('website.disablegameplay');
      break;
    case 'transactions':
      validationList.enableTransactionsSumary = !$enabled(
        'website.disabletransactionssummary',
      );
      break;
  }

  return validationList;
});

const isValid = computed(() => {
  if (!authStore.isLoggedIn) return true;
  return (
    Object.entries(validations.value).filter(([k, v]) => {
      if (!v) return k;
    }).length === 0
  );
});

const currentGame = computed(() => gameStore.currentGame);
</script>

<template>
  <slot v-if="isValid" />
  <div v-else>
    <CompleteRegistrationEmbed v-if="!validations.partialReg">
      <template #title>{{ $t('complete-registration-to-continue') }}</template>
    </CompleteRegistrationEmbed>
    <DocumentVerificaitonEmbed v-else-if="!validations.compliance" />
    <UserInterfaceErrorNotice v-else>
      <template #icon>
        <DesignSystemIconsMaintenanceIcon
          v-if="!showCustomImage"
          class="!w-20 md:!w-32"
        />
        <LazyImage
          v-else-if="!!currentGame?.image?.squareTile && showCustomImage"
          :src="currentGame?.image?.squareTile"
          :show-placeholder="false"
          class="absolute"
        />
      </template>
      <template #title>
        {{ $t(`${noticeKey}-disabled-title`) }}
      </template>
      <template #message>
        {{ $t(`${noticeKey}-disabled-message`) }}
      </template>
    </UserInterfaceErrorNotice>
  </div>
</template>
